import { Dialog, IconButton, Stack, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import FormInput from "../molecules/FormInput";
import FormTemplate from "../templates/FormTemplate";
import { ApiService, UserService } from "../../services/api";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useNavigate } from "react-router";
import DeleteIcon from "@mui/icons-material/Delete";
import useConfirm from "../../hooks/useConfirm";

interface EditApiDialogProps {
  _id: string;
  onClose?: () => void;
}

export default function EditApiDialog(props: EditApiDialogProps) {
  const { t } = useTranslation("EditApiDialog");
  const navigate = useNavigate();
  const [description, setDescription] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [user, setUser] = useState("");
  const [allowedIps, setAllowedIps] = useState<string[]>([]);
  const [key, setKey] = useState("");

  const confirm = useConfirm();

  const { data: users, isLoading: loadingUsers } = useQuery({
    queryKey: ["user-mini-list"],
    queryFn: async () => {
      const res = await UserService.getPrivilegedUserList();
      return res.data.data;
    },
  });

  const { mutate: loadApi, isPending: isLoadingApi } = useMutation({
    mutationFn: async () => {
      const res = await ApiService.getAPI({ id: props._id });
      return res.data.data;
    },
    onSuccess: (data) => {
      if (!data) return;
      console.log(data);
      setDescription(data.description || "");
      setEnabled(data.enabled || false);
      setUser(data.user?._id || "");
      setAllowedIps(data.allowedIps || []);
      setKey(data.key || "");
    },
  });

  useEffect(() => {
    if (!props._id) return;
    loadApi();
  }, [props._id]);

  const handleSubmit = async () => {
    const res = await ApiService.updateAPI({
      id: props._id,
      updateAPIBody: {
        description,
        user,
        allowedIps,
        enabled,
      },
    });
    if (res?.data?.success) {
      props.onClose?.();
    }
  };

  const { mutate: deleteApi } = useMutation({
    mutationFn: async () => {
      const res = await ApiService.deleteAPI({ id: props._id });
      return res.data;
    },
    onSuccess: (data) => {
      if (data?.success) {
        props.onClose?.();
      }
    },
  });

  const handleCopy = () => {
    navigator.clipboard.writeText(key);
  };

  return (
    <Dialog open={!!props._id} onClose={props.onClose}>
      <FormTemplate
        title={t("EditApi")}
        onSubmit={handleSubmit}
        onCancel={props.onClose}
        confirm
        options={[
          {
            label: t("Delete"),
            icon: <DeleteIcon color="error" />,
            onClick: () => confirm(deleteApi),
          },
        ]}
      >
        <Stack spacing={2} sx={{ minWidth: { xs: 300, sm: 500 } }}>
          <FormInput
            field="enabled"
            label={t("Enabled")}
            onChange={(v) => {
              setEnabled(v as boolean);
            }}
            type="switch"
            value={enabled}
          />
          <FormInput
            field="description"
            label={t("Description")}
            onChange={(v) => {
              setDescription(v as string);
            }}
            placeholder={t("DescriptionPlaceholder")}
            type="string"
            value={description}
            required
          />
          <FormInput
            field="user"
            label={t("User")}
            onChange={(v) => {
              setUser(v as string);
            }}
            placeholder={t("SelectUserPlaceholder")}
            list={(users || []).map((user) => ({
              name: user.name,
              value: user._id || "",
            }))}
            type="list"
            value={user}
            loading={loadingUsers}
            required
          />
          <FormInput
            field="allowedIps"
            label={t("AllowedIps")}
            value={allowedIps}
            placeholder={t("AllowedIpsPlaceholder")}
            type="chip-list"
            onChange={(v) => {
              setAllowedIps(v as string[]);
            }}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <FormInput
              field="apiKey"
              label=""
              value={key}
              type="string"
              inputType="password"
              readOnly
            />
            <Tooltip placement="top" title={t("Copy")}>
              <IconButton onClick={handleCopy}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </FormTemplate>
    </Dialog>
  );
}
